/**
 * Table-independent peak/off-peak window matching (#2156 / #2164).
 *
 * SQL point-in-time membership and the week×minute overlap check are the
 * same calculation whether the row is a procurement overlay or a SKU sale
 * schedule. Callers own the table name, identity predicates, selected
 * columns, and (for sale windows) `effective_from` versioning.
 *
 * The LATERAL join each side emits is built FROM these fragments — there is
 * not a second matcher in TypeScript. Overlap expansion is write-time only.
 */

import { parseUsageBucketTimeZone } from './public-usage.ts';
import {
  invalid,
  valid,
  type ContractValidationResult,
} from './validation.ts';

export const MAX_SCHEDULE_WINDOWS_PER_SET = 24;
export const MINUTES_PER_DAY = 1440;
export const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;

export const SCHEDULE_WINDOW_DAYS_OF_WEEK_MIN = 1;
export const SCHEDULE_WINDOW_DAYS_OF_WEEK_MAX = 127;
export const SCHEDULE_WINDOW_START_MINUTE_MIN = 0;
export const SCHEDULE_WINDOW_START_MINUTE_MAX = 1439;
export const SCHEDULE_WINDOW_END_MINUTE_MIN = 1;
export const SCHEDULE_WINDOW_END_MINUTE_MAX = 1440;

export interface ScheduleWindowOverlapInput {
  readonly window_id: string;
  readonly days_of_week: number;
  readonly start_minute: number;
  readonly end_minute: number;
  readonly enabled: boolean;
}

export interface WeekMinuteInterval {
  readonly start: number;
  readonly end: number;
}

export interface OverlappingWindowPair {
  readonly left_window_id: string;
  readonly right_window_id: string;
}

export interface MixedScheduleTimezones {
  readonly left: string;
  readonly right: string;
}

export interface ScheduleWindowClockFields {
  readonly timezone: string;
  readonly days_of_week: number;
  readonly start_minute: number;
  readonly end_minute: number;
}

const SQL_ALIAS = /^[A-Za-z_][A-Za-z0-9_]*$/;
const INSTANT_PLACEHOLDER = /^\$[1-9][0-9]*$/;
const INSTANT_COLUMN = /^[A-Za-z_][A-Za-z0-9_]*\.[A-Za-z_][A-Za-z0-9_]*$/;

function requireSqlAlias(value: string, label: string): string {
  if (!SQL_ALIAS.test(value)) {
    throw new Error(`${label} must be a SQL identifier, received ${JSON.stringify(value)}`);
  }
  return value;
}

function requireInstantExpr(value: string): string {
  if (INSTANT_PLACEHOLDER.test(value) || INSTANT_COLUMN.test(value)) return value;
  throw new Error(
    `instant must be a positional bind such as '$1' or a qualified column such as 'req.event_at', received ${JSON.stringify(value)}`,
  );
}

/**
 * Local wall-clock extraction. Instant → local is `AT TIME ZONE <alias>.timezone`,
 * which is defined for every timestamptz (DST spring-forward does not create a
 * gap in this direction). `instantPlaceholder` is a bound `$n` or a
 * qualified column such as `req.event_at`; never `now()`.
 *
 * Produces aliases `loc` (timestamptz-as-timestamp) and `clock` (dow, minute)
 * inside the caller's LATERAL.
 */
export function scheduleWindowClockSql(windowAlias: string, instantPlaceholder: string): string {
  const alias = requireSqlAlias(windowAlias, 'window alias');
  const instant = `${requireInstantExpr(instantPlaceholder)}::timestamptz`;
  return `CROSS JOIN LATERAL (
      SELECT (${instant} AT TIME ZONE ${alias}.timezone) AS local_ts
    ) loc
    CROSS JOIN LATERAL (
      SELECT EXTRACT(ISODOW FROM loc.local_ts)::int - 1 AS dow,
             EXTRACT(HOUR FROM loc.local_ts)::int * 60
               + EXTRACT(MINUTE FROM loc.local_ts)::int AS minute
    ) clock`;
}

/**
 * Point-in-time membership against `clock` from {@link scheduleWindowClockSql}.
 *
 * Matching:
 *   local  := $instant AT TIME ZONE w.timezone
 *   dow    := EXTRACT(ISODOW FROM local)::int - 1   -- 0=Mon … 6=Sun
 *   minute := hour*60 + minute
 *   non-wrap (start < end): bit(dow) AND minute ∈ [start, end)
 *   wrap    (start >= end): belongs to the START weekday —
 *     (bit(dow) AND minute >= start) OR (bit(previous dow) AND minute < end)
 * so Friday 23:00–01:00 still matches Saturday 01:00 as Friday's window.
 */
export function scheduleWindowMatchSql(windowAlias: string, clockAlias = 'clock'): string {
  const w = requireSqlAlias(windowAlias, 'window alias');
  const clock = requireSqlAlias(clockAlias, 'clock alias');
  return `CASE
         WHEN ${w}.start_minute < ${w}.end_minute THEN
           ((${w}.days_of_week >> ${clock}.dow) & 1) = 1
           AND ${clock}.minute >= ${w}.start_minute
           AND ${clock}.minute < ${w}.end_minute
         ELSE
           (
             ((${w}.days_of_week >> ${clock}.dow) & 1) = 1
             AND ${clock}.minute >= ${w}.start_minute
           )
           OR
           (
             ((${w}.days_of_week >> ((${clock}.dow + 6) % 7)) & 1) = 1
             AND ${clock}.minute < ${w}.end_minute
           )
       END`;
}

/** Half-open intervals on the 0..10080 week×minute ring. Empty intervals omitted. */
export function expandWindowToWeekMinuteIntervals(
  window: ScheduleWindowOverlapInput,
): readonly WeekMinuteInterval[] {
  const intervals: WeekMinuteInterval[] = [];
  for (let day = 0; day < 7; day += 1) {
    if (((window.days_of_week >> day) & 1) !== 1) continue;
    if (window.start_minute < window.end_minute) {
      pushInterval(intervals, day * MINUTES_PER_DAY + window.start_minute, day * MINUTES_PER_DAY + window.end_minute);
      continue;
    }
    const startAbs = day * MINUTES_PER_DAY + window.start_minute;
    if (day < 6) {
      pushInterval(intervals, startAbs, (day + 1) * MINUTES_PER_DAY + window.end_minute);
      continue;
    }
    pushInterval(intervals, startAbs, MINUTES_PER_WEEK);
    pushInterval(intervals, 0, window.end_minute);
  }
  return intervals;
}

/**
 * Local week-minute (0..10079, 0 = Monday 00:00) of an instant in `timezone`.
 *
 * `Intl.DateTimeFormat` is the zone database every runtime already carries, so
 * this needs no table of its own. Returns `null` for a zone the runtime does
 * not know — callers decide whether that is a refusal or a fallback.
 */
export function scheduleWindowLocalWeekMinute(
  instant: Date,
  timezone: string,
): number | null {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(instant);
  } catch {
    return null;
  }
  const read = (type: string): string | undefined =>
    parts.find((part) => part.type === type)?.value;
  // 0 = Monday, matching the `days_of_week` bit order and the SQL matcher's
  // `EXTRACT(ISODOW) - 1`.
  const weekdayIndex = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    .indexOf(read('weekday') ?? '');
  const hour = Number(read('hour'));
  const minute = Number(read('minute'));
  if (weekdayIndex < 0 || !Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  return weekdayIndex * MINUTES_PER_DAY + hour * 60 + minute;
}

/**
 * Whether a window covers an instant.
 *
 * DISPLAY ONLY. The authoritative verdict is the SQL matcher
 * ({@link scheduleWindowMatchSql}) evaluated inside the query that reads the
 * rows; this exists so an operator surface can render "what is in force right
 * now, and what will be at 03:00" without a round trip per minute.
 *
 * It is not a second matcher: membership is decided by the same
 * {@link expandWindowToWeekMinuteIntervals} expansion the write-time overlap
 * check uses, so the wrap rule cannot drift between the two TypeScript
 * callers. Only the instant → local-clock step is new, and it is the direct
 * analogue of the SQL's `AT TIME ZONE`.
 */
export function scheduleWindowCoversInstant(
  window: ScheduleWindowClockFields & { readonly enabled?: boolean },
  instant: Date,
): boolean {
  if (window.enabled === false) return false;
  const weekMinute = scheduleWindowLocalWeekMinute(instant, window.timezone);
  if (weekMinute === null) return false;
  return expandWindowToWeekMinuteIntervals({
    window_id: '',
    days_of_week: window.days_of_week,
    start_minute: window.start_minute,
    end_minute: window.end_minute,
    enabled: true,
  }).some((interval) => weekMinute >= interval.start && weekMinute < interval.end);
}

export function overlappingEnabledWindows(
  windows: readonly ScheduleWindowOverlapInput[],
): OverlappingWindowPair | null {
  const enabled = windows.filter((window) => window.enabled);
  for (let i = 0; i < enabled.length; i += 1) {
    const left = enabled[i];
    if (left === undefined) continue;
    const leftIntervals = expandWindowToWeekMinuteIntervals(left);
    for (let j = i + 1; j < enabled.length; j += 1) {
      const right = enabled[j];
      if (right === undefined) continue;
      if (intervalsOverlap(leftIntervals, expandWindowToWeekMinuteIntervals(right))) {
        return { left_window_id: left.window_id, right_window_id: right.window_id };
      }
    }
  }
  return null;
}

export function mixedScheduleTimezones(
  timezones: readonly string[],
): MixedScheduleTimezones | null {
  let first: string | undefined;
  for (const timezone of timezones) {
    if (first === undefined) {
      first = timezone;
      continue;
    }
    if (timezone !== first) return { left: first, right: timezone };
  }
  return null;
}

/**
 * Shared clock-field validation: IANA-shaped timezone, weekday mask, minute
 * range, and `start != end`. Existence of the zone in PostgreSQL stays with
 * the writer (a `pg_timezone_names` lookup). `pathPrefix` is interpolated
 * into the error the way the gateway write face already phrases it.
 */
export function validateScheduleWindowClockFields(
  input: {
    readonly timezone: unknown;
    readonly days_of_week: unknown;
    readonly start_minute: unknown;
    readonly end_minute: unknown;
  },
  pathPrefix: string,
): ContractValidationResult<ScheduleWindowClockFields> {
  if (typeof input.timezone !== 'string' || input.timezone.length === 0 || input.timezone.trim() !== input.timezone) {
    return invalid(`${pathPrefix}.timezone is required and must be an exact non-empty string`);
  }
  const parsedTimezone = parseUsageBucketTimeZone(input.timezone);
  if (!parsedTimezone.ok) {
    return invalid(`${pathPrefix}.timezone "${input.timezone}" is not a known time zone`);
  }
  if (
    !Number.isInteger(input.days_of_week)
    || typeof input.days_of_week !== 'number'
    || input.days_of_week < SCHEDULE_WINDOW_DAYS_OF_WEEK_MIN
    || input.days_of_week > SCHEDULE_WINDOW_DAYS_OF_WEEK_MAX
  ) {
    return invalid(
      `${pathPrefix}.days_of_week must be an integer between ${SCHEDULE_WINDOW_DAYS_OF_WEEK_MIN} and ${SCHEDULE_WINDOW_DAYS_OF_WEEK_MAX}`,
    );
  }
  if (
    !Number.isInteger(input.start_minute)
    || typeof input.start_minute !== 'number'
    || input.start_minute < SCHEDULE_WINDOW_START_MINUTE_MIN
    || input.start_minute > SCHEDULE_WINDOW_START_MINUTE_MAX
  ) {
    return invalid(
      `${pathPrefix}.start_minute must be an integer between ${SCHEDULE_WINDOW_START_MINUTE_MIN} and ${SCHEDULE_WINDOW_START_MINUTE_MAX}`,
    );
  }
  if (
    !Number.isInteger(input.end_minute)
    || typeof input.end_minute !== 'number'
    || input.end_minute < SCHEDULE_WINDOW_END_MINUTE_MIN
    || input.end_minute > SCHEDULE_WINDOW_END_MINUTE_MAX
  ) {
    return invalid(
      `${pathPrefix}.end_minute must be an integer between ${SCHEDULE_WINDOW_END_MINUTE_MIN} and ${SCHEDULE_WINDOW_END_MINUTE_MAX}`,
    );
  }
  if (input.start_minute === input.end_minute) {
    return invalid(`${pathPrefix} start_minute must not equal end_minute`);
  }
  return valid({
    timezone: parsedTimezone.value,
    days_of_week: input.days_of_week,
    start_minute: input.start_minute,
    end_minute: input.end_minute,
  });
}

function pushInterval(into: WeekMinuteInterval[], start: number, end: number): void {
  if (start < end) into.push({ start, end });
}

function intervalsOverlap(
  left: readonly WeekMinuteInterval[],
  right: readonly WeekMinuteInterval[],
): boolean {
  for (const a of left) {
    for (const b of right) {
      if (a.start < b.end && b.start < a.end) return true;
    }
  }
  return false;
}
